// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';
import tabMessages from '../messages';

export const NAME = 'gross-rent-certifications';

const ASSISTANCE_HEADER_SX = { backgroundColor: 'primary.dark' };

export const HEADERS = [
  {
    id: 'action',
    label: <FormattedMessage {...tabMessages.actions} />,
    sortable: false,
    searchable: false,
    dataType: 'string',
    defaultWidth: 86,
  },
  {
    id: 'unit',
    label: <FormattedMessage {...tabMessages.unit} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'floorPlan',
    label: <FormattedMessage {...tabMessages.floorPlan} />,
    sortable: true,
    searchable: false,
    dataType: 'string',
  },
  {
    id: 'headOfHousehold',
    label: <FormattedMessage {...tabMessages.headOfHousehold} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'newGrossRent',
    label: <FormattedMessage {...tabMessages.newGrossRent} />,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'newHUDUA',
    label: <FormattedMessage {...tabMessages.newHUDUA} />,
    sortable: true,
    searchable: true,
    dataType: 'number',
  },
  {
    id: 'newSubsidy',
    label: <FormattedMessage {...tabMessages.newSubsidy} />,
    sortable: true,
    searchable: true,
    dataType: 'number',
    sx: ASSISTANCE_HEADER_SX,
  },
  {
    id: 'newResidentRent',
    label: <FormattedMessage {...tabMessages.newResidentRent} />,
    sortable: true,
    searchable: true,
    dataType: 'number',
    sx: ASSISTANCE_HEADER_SX,
  },
  {
    id: 'newUtilityReimbursement',
    label: <FormattedMessage {...tabMessages.newUtilityReimbursement} />,
    sortable: true,
    searchable: true,
    dataType: 'number',
    sx: ASSISTANCE_HEADER_SX,
  },
  {
    id: 'grEffectiveDate',
    label: <FormattedMessage {...tabMessages.grEffectiveDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'anticipatedVoucherDate',
    label: <FormattedMessage {...tabMessages.anticipatedVoucherDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'priorCertificationDate',
    label: <FormattedMessage {...tabMessages.priorCertificationDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'otherCertInProgress',
    label: <FormattedMessage {...tabMessages.otherCertInProgress} />,
    sortable: true,
    searchable: false,
    dataType: 'string',
  },
  {
    id: 'occupiedOnNTV',
    label: <FormattedMessage {...tabMessages.noticeToVacate} />,
    sortable: true,
    searchable: false,
    dataType: 'string',
  },
  {
    id: 'residentSignatureRequired',
    label: <FormattedMessage {...tabMessages.residentSignatureRequired} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'generate59a',
    label: <FormattedMessage {...tabMessages.generate59a} />,
    sortable: false,
    searchable: false,
    dataType: 'string',
    defaultWidth: 158,
  },
];

export const PROPERTY_PATH_MAP = {
  unit: {
    path: 'unit',
    dataType: 'string',
  },
  floorPlan: {
    path: 'floorPlanName',
    dataType: 'string',
  },
  headOfHousehold: {
    path: 'headOfHousehold',
    dataType: 'string',
  },
  newGrossRent: {
    path: 'newGrossRent',
    dataType: 'number',
  },
  newHUDUA: {
    path: 'newHUDUA',
    dataType: 'number',
  },
  newSubsidy: {
    path: 'newSubsidy',
    dataType: 'number',
  },
  newResidentRent: {
    path: 'newResidentRent',
    dataType: 'number',
  },
  newUtilityReimbursement: {
    path: 'newUtilityReimbursement',
    dataType: 'number',
  },
  grEffectiveDate: {
    path: 'grEffectiveDate',
    dataType: 'date',
  },
  anticipatedVoucherDate: {
    path: 'anticipatedVoucherDate',
    dataType: 'date',
  },
  priorCertificationDate: {
    path: 'priorCertificationDate',
    dataType: 'date',
  },
  otherCertInProgress: {
    path: 'otherCertInProgress',
    dataType: 'string',
  },
  occupiedOnNTV: {
    path: 'occupiedOnNTV',
    dataType: 'string',
  },
  residentSignatureRequired: {
    path: 'residentSignatureRequired',
    dataType: 'string',
  },
};

// note: Table assumes TOOLBAR_HEIGHT (45px); labeled toolbar is taller
export const TABLE_HEAD_HEIGHT = 35;

export const getLabeledToolbarTableSx = (tableName: string) => ({
  gridTemplateRows: 'auto 1fr',
  minHeight: 0,
  [`& #table-container_${tableName}`]: {
    height: '100%',
    minHeight: 0,
  },
  [`& #table-body_${tableName}`]: {
    height: `calc(100% - ${TABLE_HEAD_HEIGHT}px) !important`,
    minHeight: 0,
  },
  [`& #table-scroll-container_${tableName}`]: {
    height: '100% !important',
    overflow: 'auto !important',
  },
  [`& #table-toolbar_${tableName}`]: {
    paddingY: '12px',
    alignItems: { xs: 'stretch', sm: 'flex-start' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: { xs: 1, sm: 0 },
    '& > .MuiStack-root:first-of-type': {
      flex: 1,
      minWidth: 0,
      width: { xs: '100%', sm: 'auto' },
    },
    '& > .MuiStack-root:last-of-type': {
      alignItems: 'center',
      alignSelf: { xs: 'flex-end', sm: 'auto' },
      height: { xs: 'auto', sm: '40px' },
      marginTop: { xs: 0, sm: '24px' },
    },
  },
});
